import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Measurements from "./pages/Measurements";
import MeasurementsCustomPreview from "./pages/MeasurementsCustomPreview";
import Responsibles from "./pages/Responsibles";
import Settings from "./pages/Settings";
import Turbines from "./pages/Turbines";
import Charts from "./pages/Charts";
import Automatika from "./pages/Automatika";
import Report from "./pages/responsible/Report";
import ReportForm from "./pages/responsible/ReportForm";
import EmergencyForm from "./pages/responsible/EmergencyForm";
import EmergencyLanding from "./pages/responsible/EmergencyLanding";
import MyMeasurements from "./pages/responsible/MyMeasurements";
import DesktopHint, { isInsideTelegram } from "./components/DesktopHint";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { api, ApiError } from "./api";
import { useT, normalizeLang } from "./i18n";
import type { MeOut } from "./types";


export default function App() {
  const { t, lang, setLang } = useT();
  const inside = isInsideTelegram();
  const [me, setMe] = useState<MeOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!inside) return;
    api.get<MeOut>("/api/me")
      .then((m) => {
        setMe(m);
        const serverLang = normalizeLang(m.language_code);
        if (serverLang !== lang) setLang(serverLang);
      })
      .catch((e: ApiError) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inside]);

  if (!inside) return <DesktopHint />;

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2>{error}</h2>
        <p style={{ color: "var(--hint)", fontSize: 13 }}>
          {t("access.locked_hint")}
        </p>
      </div>
    );
  }
  if (!me) {
    return <div className="empty-state" style={{ padding: 80 }}><span className="spinner" /></div>;
  }

  const isAdmin = me.role === "admin";
  const adminTabs = [
    { to: "/dashboard",    icon: "📊", label: t("tabs.dashboard") },
    { to: "/charts",       icon: "📈", label: t("tabs.charts") },
    { to: "/automatika",   icon: "🤖", label: t("tabs.automatika") },
    { to: "/measurements", icon: "📋", label: t("tabs.records") },
    { to: "/turbines",     icon: "🏭", label: t("tabs.turbines") },
    { to: "/responsibles", icon: "👥", label: t("tabs.people") },
    { to: "/settings",     icon: "⚙",  label: t("tabs.settings") },
  ];
  const responsibleTabs = [
    { to: "/report",          icon: "📝", label: t("tabs.report") },
    { to: "/emergency",       icon: "🚨", label: t("tabs.emergency") },
    { to: "/my-measurements", icon: "📋", label: t("tabs.my_records") },
  ];
  const tabs = isAdmin ? adminTabs : responsibleTabs;
  const defaultRoute = isAdmin ? "/dashboard" : "/report";

  return (
    <div className="app">
      <LanguageSwitcher />
      <main className="main">
        <Routes>
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          {isAdmin ? (
            <>
              <Route path="/dashboard"    element={<Dashboard />} />
              <Route path="/charts"       element={<Charts />} />
              <Route path="/automatika"   element={<Automatika />} />
              <Route path="/measurements" element={<Measurements />} />
              <Route path="/records/custom" element={<MeasurementsCustomPreview />} />
              <Route path="/turbines"     element={<Turbines />} />
              <Route path="/responsibles" element={<Responsibles />} />
              <Route path="/settings"     element={<Settings />} />
              {/* Shared report form — used by the admin "Fill report" button on Turbines. */}
              <Route path="/report/fill"  element={<ReportForm />} />
              <Route path="*"             element={<Navigate to="/dashboard" replace />} />
            </>
          ) : (
            <>
              <Route path="/report"           element={<Report />} />
              <Route path="/report/fill"      element={<ReportForm />} />
              <Route path="/emergency"        element={<EmergencyLanding />} />
              <Route path="/report/emergency" element={<EmergencyForm />} />
              <Route path="/my-measurements"  element={<MyMeasurements />} />
              <Route path="*"                 element={<Navigate to="/report" replace />} />
            </>
          )}
        </Routes>
      </main>
      <TabBar tabs={tabs} />
    </div>
  );
}

function TabBar({ tabs }: { tabs: { to: string; icon: string; label: string }[] }) {
  const loc = useLocation();
  const focusedPath = loc.pathname === "/report/fill" || loc.pathname === "/report/emergency";
  if (focusedPath) return null;
  return (
    <nav className="tabbar">
      {tabs.map(t => (
        <NavLink key={t.to} to={t.to}
          className={({ isActive }) => `tab${isActive ? " active" : ""}`}>
          <span className="tab-icon">{t.icon}</span>
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
